import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4 md:mb-6">
              <img 
                src="/images/assets/logo.png" 
                alt="ComfortMaster Heating & Air" 
                className="h-14 md:h-16 w-auto drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
              />
            </div>
            <p className="text-stone-400 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
              For more than 25 years, our family-focused team has been dedicated to keeping homes and businesses comfortable with honest service, expert craftsmanship, and a commitment to doing things the right way.
            </p>
            {/* Social Media Links */}
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/comfortmasterheatingandair" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors" 
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/comfortmasterheatingandair/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors" 
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@comfortmasterhvac" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors" 
                data-testid="social-tiktok"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://www.youtube.com/@comfortmasterheating" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors" 
                data-testid="social-youtube"
                aria-label="YouTube"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="https://g.page/comfortmaster-heating-air" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors" 
                data-testid="social-google"
                aria-label="Google Business"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 uppercase tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-home">Home</Link></li>
              <li><Link to="/services" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-services">Services</Link></li>
              <li><Link to="/specials" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-specials">Specials</Link></li>
              <li><Link to="/comfort-loyalty" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-loyalty">Comfort Loyalty Club</Link></li>
              <li><Link to="/blog" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-blog">Blog</Link></li>
              <li><Link to="/faq" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-faq">FAQ</Link></li>
              <li><Link to="/contact" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-contact">Contact Us</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 uppercase tracking-wide">Our Services</h3>
            <ul className="space-y-3">
              <li><Link to="/services/heating" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-heating">Heating Services</Link></li>
              <li><Link to="/services/cooling" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-cooling">Cooling Services</Link></li>
              <li><Link to="/services/installation" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-installation">HVAC Installation</Link></li>
              <li><Link to="/services/air-quality" className="text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-air-quality">Air Quality</Link></li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 uppercase tracking-wide">Service Areas</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/service-areas/lexington" className="text-stone-400 hover:text-emerald-400 transition-colors">Lexington</Link></li>
              <li><Link to="/service-areas/nicholasville" className="text-stone-400 hover:text-emerald-400 transition-colors">Nicholasville</Link></li>
              <li><Link to="/service-areas/danville" className="text-stone-400 hover:text-emerald-400 transition-colors">Danville</Link></li>
              <li><Link to="/service-areas/richmond" className="text-stone-400 hover:text-emerald-400 transition-colors">Richmond</Link></li>
              <li><Link to="/service-areas/paris" className="text-stone-400 hover:text-emerald-400 transition-colors">Paris</Link></li>
              <li><Link to="/service-areas/harrodsburg" className="text-stone-400 hover:text-emerald-400 transition-colors">Harrodsburg</Link></li>
              <li><Link to="/service-areas/winchester" className="text-stone-400 hover:text-emerald-400 transition-colors">Winchester</Link></li>
              <li><Link to="/service-areas/georgetown" className="text-stone-400 hover:text-emerald-400 transition-colors">Georgetown</Link></li>
              <li><Link to="/service-areas/midway" className="text-stone-400 hover:text-emerald-400 transition-colors">Midway</Link></li>
              <li><Link to="/service-areas/lawrenceburg" className="text-stone-400 hover:text-emerald-400 transition-colors">Lawrenceburg</Link></li>
              <li><Link to="/service-areas/versailles" className="text-stone-400 hover:text-emerald-400 transition-colors">Versailles</Link></li>
              <li><Link to="/service-areas/frankfort" className="text-stone-400 hover:text-emerald-400 transition-colors">Frankfort</Link></li>
              <li><Link to="/service-areas/lancaster" className="text-stone-400 hover:text-emerald-400 transition-colors">Lancaster</Link></li>
              <li><Link to="/service-areas/cynthiana" className="text-stone-400 hover:text-emerald-400 transition-colors">Cynthiana</Link></li>
              <li><Link to="/service-areas/mt-sterling" className="text-stone-400 hover:text-emerald-400 transition-colors">Mt Sterling</Link></li>
              <li><Link to="/service-areas/wilmore" className="text-stone-400 hover:text-emerald-400 transition-colors">Wilmore</Link></li>
              <li><Link to="/service-areas/stamping-ground" className="text-stone-400 hover:text-emerald-400 transition-colors">Stamping Ground</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 uppercase tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:8592550728" className="flex items-start gap-3 text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-phone">
                  <Phone size={20} className="text-emerald-600 flex-shrink-0 mt-1" />
                  <span>(859) 255-0728</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@comfortmasterheating.com" className="flex items-start gap-3 text-stone-400 hover:text-emerald-400 transition-colors" data-testid="footer-email">
                  <Mail size={20} className="text-emerald-600 flex-shrink-0 mt-1" />
                  <span className="min-w-0 break-all">info@comfortmasterheating.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-stone-400">
                  <MapPin size={20} className="text-emerald-600 flex-shrink-0 mt-1" />
                  <span>51 Wichita Drive<br />Nicholasville, KY 40356</span>
                </div>
              </li>
            </ul>
            {/* Best of Lexington Badge */}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <img 
                src="/images/assets/best-of-lexington-2026.webp" 
                alt="Best of Lexington 2026 Gold Winner" 
                className="w-24 h-auto"
              />
              <img 
                src="/images/assets/nate-certified.png" 
                alt="NATE Certified Technicians" 
                className="w-24 h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-stone-500 text-sm">
              © 2026 ComfortMaster Heating & Cooling – KY: #HM07021
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
              <Link to="/privacy-policy" className="text-stone-500 hover:text-emerald-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" className="text-stone-500 hover:text-emerald-400 transition-colors">Terms & Conditions</Link>
              <Link to="/website-accessibility" className="text-stone-500 hover:text-emerald-400 transition-colors">WCAG Accessibility Statement</Link>
              <span className="text-stone-500">
                Website Designed By <a href="https://bp.agency" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 transition-colors">Brand Partners</a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
